import React, { useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQuery, useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import toast from 'react-hot-toast';
import {
  ArrowLeft,
  Sparkles,
  Copy,
  ChevronDown,
  Check,
  AlertCircle,
} from 'lucide-react';
import { templatesAPI, getErrorMessage } from '@/services/api';
import type {
  PostTemplate,
  TemplateCategory,
  TemplateVariable,
  ComposeRequest,
  ComposeResult,
} from '@/types/templates';

const CATEGORY_COLORS: Record<TemplateCategory, string> = {
  hook: '#eb5757',
  body: '#2383e2',
  cta: '#4dab5a',
  hashtags: '#9b51e0',
  first_comment: '#dfab01',
  full: '#787774',
};

const SLOT_ORDER: TemplateCategory[] = ['hook', 'body', 'cta', 'hashtags', 'first_comment'];

const CATEGORY_DISPLAY_NAMES: Record<TemplateCategory, string> = {
  hook: 'Hook',
  body: 'Body',
  cta: 'CTA',
  hashtags: 'Hashtags',
  first_comment: 'First Comment',
  full: 'Full Post',
};

const TemplateComposerPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [slots, setSlots] = useState<Record<TemplateCategory, string | undefined>>({
    hook: undefined,
    body: undefined,
    cta: undefined,
    hashtags: undefined,
    first_comment: undefined,
    full: undefined,
  });

  const [variables, setVariables] = useState<Record<string, string>>({});
  const [platform, setPlatform] = useState('twitter');
  const [copiedToClipboard, setCopiedToClipboard] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState<TemplateCategory | null>(null);

  // Fetch templates for each category
  const { data: templatesData, isLoading: isLoadingTemplates } = useQuery({
    queryKey: ['templates-compose'],
    queryFn: async () => {
      const categories: TemplateCategory[] = ['hook', 'body', 'cta', 'hashtags', 'first_comment'];
      const results: Record<string, PostTemplate[]> = {};

      for (const category of categories) {
        try {
          const response = await templatesAPI.list({
            category,
            limit: 20,
            sort_by: 'usage_count',
          });
          results[category] = response.data?.templates || [];
        } catch (error) {
          console.error(`Failed to load templates for ${category}:`, error);
          results[category] = [];
        }
      }
      return results;
    },
  });

  // Extract all variables from selected templates
  const allVariables = useMemo(() => {
    const varMap: Record<string, TemplateVariable> = {};
    const selectedTemplateIds = Object.values(slots).filter(Boolean);

    if (!templatesData) return varMap;

    selectedTemplateIds.forEach((templateId) => {
      Object.values(templatesData).forEach((templates) => {
        const template = templates?.find((t) => t.id === templateId);
        if (template?.variables) {
          template.variables.forEach((v) => {
            if (!varMap[v.key]) {
              varMap[v.key] = v;
            }
          });
        }
      });
    });

    return varMap;
  }, [slots, templatesData]);

  // Build the composed text from selected templates
  const composedPreview = useMemo(() => {
    const selectedTemplateIds = Object.values(slots).filter(Boolean);
    if (selectedTemplateIds.length === 0) {
      return { text: '', first_comment: '', hashtags: [] };
    }

    if (!templatesData) {
      return { text: '', first_comment: '', hashtags: [] };
    }

    let text = '';
    let first_comment = '';
    const hashtags: Set<string> = new Set();

    SLOT_ORDER.forEach((category) => {
      const templateId = slots[category];
      if (!templateId) return;

      let template: PostTemplate | undefined;
      Object.values(templatesData).forEach((templates) => {
        if (!template) {
          template = templates?.find((t) => t.id === templateId);
        }
      });

      if (!template) return;

      // Apply variable substitutions
      let content = template.content;
      Object.entries(variables).forEach(([key, value]) => {
        content = content.replace(new RegExp(`{{${key}}}`, 'g'), value);
      });

      if (category === 'first_comment') {
        first_comment = content;
      } else if (category === 'hashtags') {
        // Add hashtags from the dedicated array
        if (template.hashtags && template.hashtags.length > 0) {
          template.hashtags.forEach((tag) => {
            // Strip leading # if already present to avoid ##
            const clean = tag.startsWith('#') ? tag.slice(1) : tag;
            if (clean) hashtags.add(clean);
          });
        }
        // Also parse any hashtags from the content field
        if (content) {
          const matches = content.match(/#[\w]+/g);
          if (matches) {
            matches.forEach((tag) => {
              const clean = tag.startsWith('#') ? tag.slice(1) : tag;
              if (clean) hashtags.add(clean);
            });
          }
        }
      } else {
        if (text && category !== 'hook') {
          text += '\n\n';
        }
        text += content;
      }
    });

    return {
      text,
      first_comment: first_comment || undefined,
      hashtags: Array.from(hashtags),
    };
  }, [slots, templatesData, variables]);

  // Compose mutation
  const composeMutation = useMutation({
    mutationFn: async () => {
      const selectedTemplateIds = Object.values(slots).filter(Boolean) as string[];
      if (selectedTemplateIds.length === 0) {
        throw new Error(t('compose_error_no_templates', 'Select at least one template'));
      }

      const request: ComposeRequest = {
        template_ids: selectedTemplateIds,
        variables: Object.keys(variables).length > 0 ? variables : undefined,
        platform,
      };

      const response = await templatesAPI.compose(request);
      return response.data as ComposeResult;
    },
    onSuccess: (_data) => {
      toast.success(t('compose_success', 'Post composed successfully'));
      // Could navigate to /compose with the content, but for now just stay and show success
    },
    onError: (error) => {
      toast.error(getErrorMessage(error, t('compose_error', 'Failed to compose post')));
    },
  });

  const handleSelectTemplate = useCallback(
    (category: TemplateCategory, templateId: string) => {
      setSlots((prev) => ({
        ...prev,
        [category]: prev[category] === templateId ? undefined : templateId,
      }));
      setActiveDropdown(null);
    },
    []
  );

  const handleVariableChange = useCallback(
    (key: string, value: string) => {
      setVariables((prev) => ({
        ...prev,
        [key]: value,
      }));
    },
    []
  );

  const handleCopyToClipboard = useCallback(() => {
    navigator.clipboard.writeText(composedPreview.text);
    setCopiedToClipboard(true);
    toast.success(t('copied_to_clipboard', 'Copied to clipboard'));
    setTimeout(() => setCopiedToClipboard(false), 2000);
  }, [composedPreview.text, t]);

  const getSelectedTemplate = useCallback(
    (category: TemplateCategory): PostTemplate | undefined => {
      const templateId = slots[category];
      if (!templateId || !templatesData) return undefined;

      for (const templates of Object.values(templatesData)) {
        const template = templates?.find((t) => t.id === templateId);
        if (template) return template;
      }
      return undefined;
    },
    [slots, templatesData]
  );

  const getCategoryTemplates = useCallback(
    (category: TemplateCategory): PostTemplate[] => {
      return (templatesData?.[category] || []) as PostTemplate[];
    },
    [templatesData]
  );

  return (
    <div className="h-full flex flex-col bg-n-card">
      {/* Page Header */}
      <div className="flex-shrink-0 px-6 pt-6 pb-4">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-4xl font-bold text-n-text flex items-center gap-2">
              <Sparkles size={28} className="text-yellow-500" />
              {t('compose_title', 'Compose Post from Templates')}
            </h1>
            <p className="text-sm text-n-text-md mt-1">
              {t('compose_subtitle', 'Stack templates to build a complete post')}
            </p>
          </div>
          <button
            onClick={() => navigate('/templates')}
            className="flex items-center gap-2 px-4 py-2 rounded-lg bg-n-hover text-n-text hover:bg-n-border transition-colors"
          >
            <ArrowLeft size={16} />
            {t('back_btn', 'Back')}
          </button>
        </div>
      </div>

      {/* Content */}
      <div className="flex-1 min-h-0 overflow-y-auto px-6 pb-6">
        <div className="grid grid-cols-1 lg:grid-cols-5 gap-4">
          {/* Left: Composition Slots (60%) */}
          <div className="lg:col-span-3 space-y-4">
            {isLoadingTemplates ? (
              <div className="flex items-center justify-center py-12">
                <div className="h-8 w-8 animate-spin rounded-full border-2 border-n-border border-t-blue-500" />
              </div>
            ) : (
              <>
                {SLOT_ORDER.map((category) => {
                  const selectedTemplate = getSelectedTemplate(category);
                  const categoryColor = CATEGORY_COLORS[category];

                  return (
                    <div
                      key={category}
                      className="bg-n-card border border-n-border rounded-lg p-4"
                    >
                      {/* Slot Header */}
                      <div className="flex items-center gap-3 mb-3">
                        <div
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: categoryColor }}
                        />
                        <span className="text-sm font-semibold text-n-text">
                          {t(`template_category_${category}`, CATEGORY_DISPLAY_NAMES[category] || category)}
                        </span>
                        <div className="ml-auto relative">
                          <button
                            onClick={() =>
                              setActiveDropdown(
                                activeDropdown === category ? null : category
                              )
                            }
                            className="flex items-center gap-1 text-xs font-semibold px-3 py-1.5 rounded bg-n-hover text-n-text hover:bg-n-border transition-colors"
                          >
                            {t('select_btn', 'Select')}
                            <ChevronDown
                              size={14}
                              className={`transition-transform ${
                                activeDropdown === category ? 'rotate-180' : ''
                              }`}
                            />
                          </button>

                          {/* Dropdown */}
                          {activeDropdown === category && (
                            <div className="absolute right-0 top-full mt-2 bg-n-card border border-n-border rounded-lg shadow-lg z-50 w-64 max-h-48 overflow-y-auto">
                              {getCategoryTemplates(category).length === 0 ? (
                                <div className="px-4 py-8 text-center">
                                  <p className="text-xs text-n-text-lt">
                                    {t('no_templates', 'No templates available')}
                                  </p>
                                </div>
                              ) : (
                                <div className="space-y-1 p-2">
                                  {getCategoryTemplates(category).map((template) => (
                                    <button
                                      key={template.id}
                                      onClick={() =>
                                        handleSelectTemplate(category, template.id)
                                      }
                                      className={`w-full text-left px-3 py-2 rounded text-xs transition-colors ${
                                        slots[category] === template.id
                                          ? 'bg-blue-100 text-blue-700 font-semibold'
                                          : 'text-n-text hover:bg-n-hover'
                                      }`}
                                    >
                                      <div className="font-medium line-clamp-1">
                                        {template.name}
                                      </div>
                                      <div className="text-[10px] text-n-text-lt line-clamp-1 mt-0.5">
                                        {template.content}
                                      </div>
                                    </button>
                                  ))}
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Selected Template Preview */}
                      {selectedTemplate ? (
                        <div className="bg-n-page border border-n-border rounded px-3 py-2">
                          {category === 'hashtags' ? (
                            <p className="text-xs text-n-text line-clamp-2">
                              {selectedTemplate.hashtags && selectedTemplate.hashtags.length > 0
                                ? selectedTemplate.hashtags.map((tag) => tag.startsWith('#') ? tag : `#${tag}`).join(' ')
                                : selectedTemplate.content || t('compose_no_hashtags', 'No hashtags')}
                            </p>
                          ) : (
                            <p className="text-xs text-n-text line-clamp-2">
                              {selectedTemplate.content || t('compose_empty_content', '(Empty)')}
                            </p>
                          )}
                        </div>
                      ) : (
                        <div className="bg-n-page border border-dashed border-n-border rounded px-3 py-2">
                          <p className="text-xs text-n-text-lt italic">
                            {t('compose_empty_slot', 'Click to add a template')}
                          </p>
                        </div>
                      )}
                    </div>
                  );
                })}
              </>
            )}
          </div>

          {/* Right: Preview (40%) */}
          <div className="lg:col-span-2 flex flex-col gap-4">
            {/* Live Preview Card */}
            <div className="bg-n-card border border-n-border rounded-lg p-4 flex-1 flex flex-col">
              <h3 className="text-sm font-semibold text-n-text mb-3 flex items-center gap-2">
                <span>📋</span>
                {t('compose_preview', 'Live Preview')}
              </h3>

              {/* Platform Selector */}
              <div className="mb-4">
                <label className="text-xs font-semibold text-n-text-md block mb-2">
                  {t('platform', 'Platform')}
                </label>
                <select
                  value={platform}
                  onChange={(e) => setPlatform(e.target.value)}
                  className="w-full text-xs px-2 py-1.5 rounded bg-n-page border border-n-border text-n-text"
                >
                  <option value="twitter">Twitter / X</option>
                  <option value="instagram">Instagram</option>
                  <option value="facebook">Facebook</option>
                  <option value="linkedin">LinkedIn</option>
                  <option value="tiktok">TikTok</option>
                  <option value="youtube">YouTube</option>
                  <option value="pinterest">Pinterest</option>
                  <option value="bluesky">Bluesky</option>
                </select>
              </div>

              {/* Preview Text */}
              <div className="mb-4">
                <div className="h-px bg-n-border mb-3" />
                {composedPreview.text ? (
                  <p className="text-xs text-n-text whitespace-pre-wrap line-clamp-8 mb-4">
                    {composedPreview.text}
                  </p>
                ) : (
                  <p className="text-xs text-n-text-lt italic mb-4">
                    {t('compose_empty_preview', 'Select templates to preview')}
                  </p>
                )}

                {composedPreview.first_comment && (
                  <>
                    <div className="h-px bg-n-border mb-3" />
                    <div>
                      <p className="text-[10px] font-semibold text-n-text-md mb-1">
                        {t('first_comment_label', 'First Comment')}
                      </p>
                      <p className="text-xs text-n-text whitespace-pre-wrap line-clamp-3">
                        {composedPreview.first_comment}
                      </p>
                    </div>
                  </>
                )}

                {composedPreview.hashtags.length > 0 && (
                  <>
                    <div className="h-px bg-n-border my-3" />
                    <p className="text-xs text-n-text line-clamp-2">
                      {composedPreview.hashtags.map((tag) => `#${tag}`).join(' ')}
                    </p>
                  </>
                )}
              </div>

              <div className="flex-1" />

              {/* Variables Section */}
              {Object.keys(allVariables).length > 0 && (
                <div className="mt-4 pt-4 border-t border-n-border space-y-3">
                  <p className="text-xs font-semibold text-n-text-md">
                    {t('compose_variables', 'Variables')}
                  </p>
                  {Object.entries(allVariables).map(([key, variable]) => (
                    <div key={key}>
                      <label className="text-[10px] font-semibold text-n-text-md block mb-1">
                        {variable.label || key}
                      </label>
                      <input
                        type="text"
                        value={variables[key] || variable.default || ''}
                        onChange={(e) => handleVariableChange(key, e.target.value)}
                        placeholder={variable.default || ''}
                        className="w-full text-xs px-2 py-1.5 rounded bg-n-page border border-n-border text-n-text placeholder-n-text-lt"
                      />
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Warning if no templates selected */}
            {Object.values(slots).every((v) => !v) && (
              <div className="flex items-start gap-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                <AlertCircle size={14} className="text-yellow-600 flex-shrink-0 mt-0.5" />
                <p className="text-xs text-yellow-700">
                  {t('compose_warning', 'Select at least one template to begin')}
                </p>
              </div>
            )}

            {/* Action Buttons */}
            <div className="flex items-center gap-3">
              <button
                onClick={handleCopyToClipboard}
                disabled={!composedPreview.text}
                className="flex items-center gap-2 text-xs font-semibold px-4 py-2 rounded border border-n-border text-n-text hover:bg-n-hover transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex-1"
              >
                <Copy size={14} />
                {copiedToClipboard ? t('copied_btn', 'Copied') : t('copy_btn', 'Copy')}
              </button>

              <button
                onClick={() => composeMutation.mutate()}
                disabled={
                  composeMutation.isPending ||
                  Object.values(slots).every((v) => !v)
                }
                style={{ backgroundColor: '#2383e2' }}
                className="flex items-center gap-2 text-xs font-semibold text-white px-4 py-2 rounded hover:opacity-90 transition-opacity disabled:opacity-40 disabled:cursor-not-allowed flex-1"
              >
                {composeMutation.isPending ? (
                  <>
                    <div className="h-3 w-3 animate-spin rounded-full border-2 border-white border-t-transparent" />
                    {t('composing', 'Composing...')}
                  </>
                ) : (
                  <>
                    <Check size={14} />
                    {t('compose_btn', 'Compose')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TemplateComposerPage;
